import dayjs from 'dayjs';
import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from 'react-query';
import Cookies from 'universal-cookie';

import { PrimaryButton, ProButton } from '@components/Button/variants';
import IconClose from '@components/Images/Rebranding/IconClose';
import LogoTIProWithIconTI from '@components/Images/Rebranding/LogoTIProWithIconTI';
import LogoTIWithIconTI from '@components/Images/Rebranding/LogoTIWithIconTI';
import StepProfile from '@components/UserOnboardingModal/StepProfile/index';
import useProfileUpdate from '@hooks/useProfileUpdate';
import Modal from '@mui/material/Modal';
import { User } from '@utils/backend-api/currentUser';

const queryClient = new QueryClient();

interface UpdateProfileModalProps {
  initialCurrentUser: User;
  isProSubscriber: boolean;
}

const cookies = new Cookies();
const PROFILE_MODAL = 'update-profile-modal';

const UpdateProfileModalContent = ({
  initialCurrentUser,
  isProSubscriber,
}: UpdateProfileModalProps) => {
  const seenModaLast14Days = cookies.get(PROFILE_MODAL);
  const [isOpen, setIsOpen] = useState(!seenModaLast14Days);
  const [isSaving, setIsSaving] = useState(false);
  const stepSaveRef = useRef<(() => void | Promise<void>) | null>(null);

  const { saveProfile } = useProfileUpdate({ userId: initialCurrentUser.id });

  const Button = isProSubscriber ? ProButton : PrimaryButton;

  const handleStepComplete = () => {
    toast.success('Profile updated successfully');
    setIsOpen(false);
  };

  const handleContinue = async () => {
    const saveFn = stepSaveRef.current;
    setIsSaving(true);

    try {
      if (saveFn && typeof saveFn === 'function') {
        await Promise.resolve(saveFn());
      }
    } catch (err) {
      console.error('Profile save error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    cookies.set(PROFILE_MODAL, true, {
      expires: dayjs().add(14, 'days').toDate(),
    });
    setIsOpen(false);
  };

  return (
    <Modal className="OnboardingModal" open={isOpen} onClose={handleClose} disableAutoFocus={true}>
      <div data-testid="update-profile-modal" className="onboarding">
        <div className="onboarding-header border-none">
          <IconClose
            className="absolute right-10 top-10 h-20 cursor-pointer"
            onClick={handleClose}
          />
          {isProSubscriber ? (
            <LogoTIProWithIconTI className="h-[35px] w-auto" />
          ) : (
            <LogoTIWithIconTI className="h-[35px] w-auto" />
          )}
        </div>

        <p className="m-0 hidden bg-primary-magenta py-16 text-center font-suisse-neue text-overline-md text-white sm:block">
          Before continuing please update your profile, we’ll provide articles closer to your
          interests.
        </p>

        <div className="flex flex-col items-center overflow-scroll px-16 py-24">
          <h2 className="mb-8 mt-0 text-center font-suisse-works text-headline-md">
            Update Your Member Profile
          </h2>
          <StepProfile
            user={initialCurrentUser}
            saveProfile={saveProfile}
            onStepComplete={handleStepComplete}
            stepSaveRef={stepSaveRef}
          />
          <div className="mt-24 flex w-full justify-center">
            <Button as="button" onClick={handleContinue} disabled={isSaving} className="!w-[340px]">
              {isSaving ? 'Saving…' : 'Continue'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const UpdateProfileModal = (props: UpdateProfileModalProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      <UpdateProfileModalContent {...props} />
    </QueryClientProvider>
  );
};

export default UpdateProfileModal;
